import { computed } from "@vue/composition-api";
import { SelectItem } from "@/components/Atoms/OccSelectbox/types";

import {
  ChangableLotteryEventStatus,
  ChangableUnlotteryEventStatus,
  PartnerEntryUserEventStatus,
  PartnerEntryUserSelectionStatus
} from "@/api/types";
import { formatDate } from "./date-helper";

export const changeableLotteryEventStatusMapping: {
  [key in ChangableLotteryEventStatus]: string;
} = {
  elected: "当選",
  defeated: "落選",
  attended: "出席",
  absented: "欠席"
} as const;

export const changeableUnlotteryEventStatusMapping: {
  [key in ChangableUnlotteryEventStatus]: string;
} = {
  attended: "出席",
  absented: "欠席"
} as const;

export const allLotteryEventStatusMapping: {
  [key in PartnerEntryUserEventStatus]: string;
} = {
  invited: "特別案内",
  pre_entried: "レジュメ提出待ち",
  entried: "抽選中",
  elected: "当選（参加予定）",
  defeated: "落選",
  absented: "欠席",
  attended: "出席済み",
  canceled: "キャンセル"
} as const;

export const allUnlotteryEventStatusMapping: {
  [key in PartnerEntryUserEventStatus]: string;
} = {
  invited: "特別案内",
  pre_entried: "レジュメ提出待ち",
  entried: "参加予定",
  elected: "当選（参加予定）",
  defeated: "落選",
  absented: "欠席",
  attended: "出席済み",
  canceled: "キャンセル"
} as const;

export const allEventStatusSearchOptions: SelectItem<string>[] = [
  { label: "特別案内", value: "invited" },
  { label: "レジュメ提出待ち", value: "pre_entried" },
  { label: "参加予定", value: "unlottery_entried" },
  { label: "抽選中", value: "lottery_entried" },
  { label: "当選（参加予定）", value: "elected" },
  { label: "落選", value: "defeated" },
  { label: "欠席", value: "absented" },
  { label: "出席済み", value: "attended" },
  { label: "キャンセル", value: "canceled" }
];

export const selectionStatusMapping: {
  [key in PartnerEntryUserSelectionStatus]: string;
} = {
  entried: "選考中",
  unofficial_offered: "内定",
  approved: "内定承諾",
  declined: "内定辞退",
  rejected: "除外"
} as const;

export const getDisplayEventStatus = (
  status: PartnerEntryUserEventStatus,
  lottery: boolean
): string => {
  return lottery
    ? allLotteryEventStatusMapping[status]
    : allUnlotteryEventStatusMapping[status];
};

export const getDisplaySelectionStatus = (
  status: PartnerEntryUserSelectionStatus
): string => {
  return selectionStatusMapping[status];
};

export const getDisplayScheduleSpan = (date: {
  start: string;
  end: string;
}) => {
  if (date.start && date.end) {
    return `${formatDate(date.start)}〜${formatDate(date.end)}`;
  } else if (date.start) {
    return formatDate(date.start);
  } else {
    return formatDate(date.end);
  }
};

export const getDisplayEventType = (type: string) => {
  switch (type) {
    case "seminar":
      return "説明会";
    case "internship":
      return "インターン";
    case "entry":
      return "本選考";
  }
};

export const selectListGenerator = (
  list: string[],
  labelMapping?: { [key: string]: string }
) => {
  return list.map(v => {
    const label = labelMapping ? labelMapping[v] : v;
    return {
      value: v,
      label: label
    };
  });
};

export const usePartnerEntryUsers = () => {
  const allLotteryEventStatus = computed<
    SelectItem<PartnerEntryUserEventStatus>[]
  >(() => {
    type mappingKey = keyof typeof allLotteryEventStatusMapping;
    return (Object.keys(allLotteryEventStatusMapping) as mappingKey[]).map(
      key => {
        return { value: key, label: allLotteryEventStatusMapping[key] };
      }
    );
  });

  const changeableLotteryEventStatus = computed<
    SelectItem<ChangableLotteryEventStatus>[]
  >(() => {
    type mappingKey = keyof typeof changeableLotteryEventStatusMapping;
    return (Object.keys(
      changeableLotteryEventStatusMapping
    ) as mappingKey[]).map(key => {
      return { value: key, label: changeableLotteryEventStatusMapping[key] };
    });
  });

  const changeableUnlotteryEventStatus = computed<
    SelectItem<ChangableUnlotteryEventStatus>[]
  >(() => {
    type mappingKey = keyof typeof changeableUnlotteryEventStatusMapping;
    return (Object.keys(
      changeableUnlotteryEventStatusMapping
    ) as mappingKey[]).map(key => {
      return { value: key, label: changeableUnlotteryEventStatusMapping[key] };
    });
  });

  const selectionStatus = computed<
    SelectItem<PartnerEntryUserSelectionStatus>[]
  >(() => {
    type mappingKey = keyof typeof selectionStatusMapping;
    return (Object.keys(selectionStatusMapping) as mappingKey[]).map(key => {
      return { value: key, label: selectionStatusMapping[key] };
    });
  });

  return {
    allLotteryEventStatus,
    changeableLotteryEventStatus,
    changeableUnlotteryEventStatus,
    selectionStatus
  };
};
